import { useEffect, useMemo, useRef, useState, ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/auth-context'
import { DISABLE_AUTH } from '@/lib/flags'

interface RequireAuthProps {
  role?: string
  allowedRoles?: string[]
  children: ReactNode
}

export default function RequireAuth({ role, allowedRoles, children }: RequireAuthProps) {
  if (DISABLE_AUTH) {
    return <>{children}</>
  }

  const navigate = useNavigate()
  const location = useLocation()
  const { session, profile, status, error } = useAuth()
  const [message, setMessage] = useState('Checking access…')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const targetRoles = useMemo(() => {
    if (Array.isArray(allowedRoles) && allowedRoles.length) {
      return allowedRoles
    }
    return role ? [role] : null
  }, [allowedRoles, role])

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current)

    if (status === 'checking') {
      timerRef.current = setTimeout(() => setMessage('Taking longer than expected…'), 4000)
    }

    if (status === 'error') {
      return () => { if (timerRef.current) clearTimeout(timerRef.current) }
    }

    if (status !== 'checking' && !session) {
      const next = encodeURIComponent(`${location.pathname}${location.search}` || '/')
      navigate(`/login?notice=signin-required&next=${next}`, { replace: true })
      return undefined
    }

    if (status !== 'checking' && session && targetRoles?.length) {
      const currentRole = profile?.role
      // Strict: deny if role is missing OR not in allowed list (superadmin always passes)
      const hasAccess = currentRole === 'superadmin' || (currentRole && targetRoles.includes(currentRole))
      if (!hasAccess) {
        const next = encodeURIComponent(`${location.pathname}${location.search}` || '/')
        navigate(`/login?notice=role-mismatch&next=${next}`, { replace: true })
      }
    }

    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [location.pathname, location.search, navigate, profile?.role, session, status, targetRoles])

  if (status === 'checking') {
    return <div className="p-6 text-center text-cocoa/70">{message}</div>
  }

  if (status === 'error') {
    const next = encodeURIComponent(`${location.pathname}${location.search}` || '/')
    return (
      <div className="p-6 text-center">
        <div className="mb-2 font-medium text-red-600">Couldn’t verify access.</div>
        <div className="text-sm text-cocoa/70">{error || 'Unknown error'}</div>
        <a className="mt-3 inline-block underline" href={`/login?notice=retry&next=${next}`}>
          Try again
        </a>
      </div>
    )
  }

  return <>{children}</>
}
